import { useCallback } from "react";
import { TellescopeModelFromName, useTellescopeQueryClient } from "@tellescope/tanstack-query";
import { useNavigate } from "react-router-dom";
import { buildMessagingThreadLink } from "../utils/build-messaging-thread-link";
import { useUpdateEnduserInSidebar } from "./useUpdateEnduserInSidebar";

/**
 * Hook that returns a function to open an SMS thread for a given SMS message.
 *
 * This hook:
 * 1. Gets the queryClient and navigate internally
 * 2. Returns a callback that fetches the thread and navigates to it
 * 3. Extracts the enduserId from the SMS message
 * 4. Queries for the matching thread using load_threads with filters
 * 5. Navigates to the thread URL if found
 *
 * @returns A function that accepts an SMS message record and opens its thread
 */
export const useOpenSMSThread = () => {
  const queryClient = useTellescopeQueryClient();
  const navigate = useNavigate();
  const updateEnduserInSidebar = useUpdateEnduserInSidebar();

  const openSMSThread = useCallback(
    async (sms: TellescopeModelFromName<"sms_messages">, draftId?: string): Promise<void> => {
      try {
        // Fetch the thread using load_threads
        const result = await queryClient.fetchInfiniteQuery({
          model: "inbox_threads",
          method: "load_threads",
          request: {
            autobuild: true,
            limit: 1,
            mdbFilter: {
              type: "SMS",
              enduserPhoneNumber: sms.enduserPhoneNumber,
              phoneNumber: sms.phoneNumber,
              enduserIds: sms.enduserId,
            },
          },
          getNextPageParam: (lastPage) => lastPage.threads.at(-1)?.timestamp,
          initialPageParam: undefined,
          paginateRequest: (lastTimestamp, request) => ({ ...request, lastTimestamp }),
        });

        // Extract the first thread from the result
        const thread = result.pages[0]?.threads[0];

        if (!thread) {
          console.warn("No thread found for SMS message:", sms.id);
          return;
        }

        // Navigate to the thread
        const threadLink = buildMessagingThreadLink({
          view: "inbox",
          channel: "SMS",
          threadId: thread.id,
          draftId: draftId || (sms.isDraft ? sms.id : undefined),
        });

        const enduserId = thread.enduserIds?.[0];
        updateEnduserInSidebar(enduserId);

        navigate(threadLink);
      } catch (error) {
        console.error("Error opening SMS thread:", error);
      }
    },
    [navigate, queryClient, updateEnduserInSidebar]
  );

  return openSMSThread;
};
